import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { PROJECT_TYPES } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  number_of_songs: 1,
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [eventDate, setEventDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isLive = form.project_type === "Live Recording";

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type) {
      toast.error("Please fill in your name, email and project type.");
      return;
    }
    if (isLive && !eventDate) {
      toast.error("Please select an event date for a Live Recording.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_type: form.project_type,
        number_of_songs: Number(form.number_of_songs) || 1,
        event_date: eventDate ? format(eventDate, "yyyy-MM-dd") : null,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      toast.success("Quote request sent. CJ will be in touch shortly.");
      setForm(emptyForm);
      setEventDate(undefined);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="quote-success"
        className="rounded-3xl border border-white/10 bg-gradient-to-br from-brand-magenta/10 via-transparent to-brand-orange/10 p-10 lg:p-14 text-center"
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-6">
          <CheckCircle2 size={32} className="text-brand-magenta" />
        </div>
        <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tight text-white">
          Request received.
        </h3>
        <p className="mt-4 font-body text-white/65 max-w-md mx-auto">
          Thanks for reaching out. CJ will review your project and respond
          from <span className="text-white">cj@wereonesound.com</span> within
          1–2 business days.
        </p>
        <Button
          type="button"
          data-testid="quote-success-reset"
          onClick={() => setSubmitted(false)}
          className="mt-8 rounded-full bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
        >
          Submit another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-10 lg:p-12 backdrop-blur-md"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label
            htmlFor="q-name"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
          >
            Your Name
          </Label>
          <Input
            id="q-name"
            data-testid="quote-input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Artist"
            required
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus-visible:ring-brand-magenta"
          />
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="q-email"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
          >
            Email
          </Label>
          <Input
            id="q-email"
            data-testid="quote-input-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@artist.com"
            required
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus-visible:ring-brand-magenta"
          />
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="q-phone"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
          >
            Phone (optional)
          </Label>
          <Input
            id="q-phone"
            data-testid="quote-input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+1 (555) 555-5555"
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus-visible:ring-brand-magenta"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
            Project Type
          </Label>
          <Select
            value={form.project_type}
            onValueChange={(v) => update("project_type", v)}
          >
            <SelectTrigger
              data-testid="quote-select-project-type"
              className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus:ring-brand-magenta"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-white">
              {PROJECT_TYPES.map((t) => (
                <SelectItem
                  key={t}
                  value={t}
                  data-testid={`quote-project-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className="focus:bg-white/10 focus:text-white"
                >
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="q-songs"
            className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
          >
            Number of Songs
          </Label>
          <Input
            id="q-songs"
            data-testid="quote-input-songs"
            type="number"
            min={1}
            max={500}
            value={form.number_of_songs}
            onChange={(e) => update("number_of_songs", e.target.value)}
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus-visible:ring-brand-magenta"
          />
        </div>

        {isLive && (
          <div className="space-y-2" data-testid="quote-event-date-wrap">
            <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-brand-orange">
              Event Date (required)
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="quote-event-date-trigger"
                  className={cn(
                    "h-12 rounded-xl w-full justify-start text-left font-normal bg-black/40 border-white/10 text-white hover:bg-black/60 hover:text-white",
                    !eventDate && "text-white/40"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {eventDate ? format(eventDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-zinc-950 border-white/10"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={eventDate}
                  onSelect={setEventDate}
                  initialFocus
                  className="text-white"
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-2">
        <Label
          htmlFor="q-msg"
          className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60"
        >
          Tell CJ about the project
        </Label>
        <Textarea
          id="q-msg"
          data-testid="quote-input-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Genre, vibe references, deadlines, venue (for live), budget range, anything else..."
          rows={5}
          className="bg-black/40 border-white/10 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-brand-magenta"
        />
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="font-body text-xs text-white/40 max-w-md">
          By submitting, you agree to be contacted at the email/phone you
          provided. We don't share your details.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit"
          className="group rounded-full h-12 px-7 bg-white text-black font-display font-bold hover:bg-brand-magenta hover:text-white transition-all disabled:opacity-60"
        >
          {submitting ? "Sending..." : "Send Request"}
          <Send
            size={16}
            className="ml-2 group-hover:translate-x-1 transition-transform"
          />
        </Button>
      </div>
    </form>
  );
};
